import { ArrowForwardIos, Check, PlayArrowRounded } from '@mui/icons-material';
import { PlayingAnimation } from '@rhg/components/animations/playing-animation';
import { CardVariant } from '@rhg/components/card/types';
import { useCarePlanTopRecommendationSection } from '@rhg/hooks/use-care-plan-top-recommendation-section';
import { useIsMobile } from '@rhg/hooks/use-is-mobile';
import { useProvidedProgramContext } from '@rhg/hooks/use-provided-program-context';
import commonMessages from '@rhg/lang/messages/common';
import programMessages from '@rhg/lang/messages/program';
import { Button, Stack, Box } from '@rhg/libs/material';
import { Restart } from '@rhg/libs/material/icons';
import { ProgramState } from '@rhg/utils/programs';
import { useIntl } from 'react-intl';

interface BannerActionsProps {
    variant: CardVariant;
    durationText: string;
    isPlaying?: boolean;
    playHandler: () => void;
    joinAction?: () => void;
    detailsRoute?: string;
}

const styleButtonStack = {
    flexWrap: 'wrap',
};

const styleButtonGrow = {
    flexGrow: 1,
    textWrap: 'nowrap',
};

const stylePlayingAnimation = {
    '& svg': {
        maxHeight: 24,
    },
};

export const BannerActions = ({
    joinAction,
    playHandler,
    variant,
    durationText,
    detailsRoute,
    isPlaying,
}: BannerActionsProps) => {
    const { hasJoinedOtherPrograms } = useCarePlanTopRecommendationSection();
    const { currentSession, programState } = useProvidedProgramContext();
    const { formatMessage } = useIntl();
    const isMobile = useIsMobile();
    const isJoinActionDisplayed =
        typeof joinAction !== 'undefined' &&
        !hasJoinedOtherPrograms &&
        (programState === ProgramState.NotJoined ||
            programState === ProgramState.Abandoned ||
            programState === ProgramState.Completed);
    const isCompleted = programState === ProgramState.Completed;
    const isInProgress = programState === ProgramState.InProgress;

    return (
        <Stack direction="row" gap={4} sx={styleButtonStack}>
            {isJoinActionDisplayed && (
                <Button
                    trackButton={
                        isCompleted ? 'CtaRestartProgram' : 'CtaJoinProgram'
                    }
                    size="large"
                    variant="contained"
                    startIcon={isCompleted ? <Restart /> : <Check />}
                    onClick={joinAction}
                    sx={styleButtonGrow}
                >
                    {isCompleted
                        ? formatMessage(
                              programMessages['program.restart_program']
                          )
                        : formatMessage(
                              programMessages['program.join_program']
                          )}
                </Button>
            )}
            {isInProgress && (
                <Button
                    trackButton="ProgramPlay"
                    startIcon={
                        !isJoinActionDisplayed ? (
                            isPlaying && variant === 'bannerV2' ? (
                                <Box sx={stylePlayingAnimation}>
                                    <PlayingAnimation />
                                </Box>
                            ) : (
                                <PlayArrowRounded />
                            )
                        ) : undefined
                    }
                    variant={isJoinActionDisplayed ? 'outlined' : 'contained'}
                    color="primary"
                    size="large"
                    onClick={playHandler}
                    sx={styleButtonGrow}
                >
                    {variant === 'bannerV2'
                        ? formatMessage(
                              commonMessages['common.play_session_number'],
                              {
                                  number: currentSession?.position,
                              }
                          )
                        : durationText}
                </Button>
            )}
            {variant === 'bannerV2' ? (
                <>
                    {!isInProgress && !isMobile && (
                        <Button
                            trackButton="ProgramViewDetails"
                            endIcon={
                                !isJoinActionDisplayed && !isInProgress ? (
                                    <ArrowForwardIos />
                                ) : undefined
                            }
                            variant={'outlined'}
                            color="primary"
                            size="large"
                            disabled={!detailsRoute}
                            href={detailsRoute}
                            sx={styleButtonGrow}
                        >
                            {formatMessage(
                                commonMessages['common.view_program']
                            )}
                        </Button>
                    )}
                </>
            ) : (
                <Button
                    trackButton="ProgramViewDetails"
                    endIcon={
                        !isJoinActionDisplayed && !isInProgress ? (
                            <ArrowForwardIos />
                        ) : undefined
                    }
                    variant={
                        isJoinActionDisplayed || isInProgress
                            ? 'outlined'
                            : 'contained'
                    }
                    color="primary"
                    size="large"
                    disabled={!detailsRoute}
                    href={detailsRoute}
                    sx={styleButtonGrow}
                >
                    {formatMessage(commonMessages['common.view_details'])}
                </Button>
            )}
        </Stack>
    );
};
